import { useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { X, CalendarCheck, Save, Trash2, Edit2 } from 'lucide-react';
import { format } from 'date-fns';

function BookingCalendar() {
  const [bookings, setBookings] = useState([
    {
      id: '1',
      title: 'Booking: Jane Doe',
      start: '2025-05-10T10:00:00',
      end: '2025-05-10T11:00:00',
      description: 'Consultation about project requirements.',
    },
    {
      id: '2',
      title: 'Booking: John Smith',
      start: '2025-05-12T14:00:00',
      end: '2025-05-12T15:00:00',
      description: 'Follow-up session.',
    },
  ]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add' or 'view'
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [formData, setFormData] = useState({
    clientName: '',
    startTime: '',
    endTime: '',
    description: '',
  });

  // Open Add Booking Drawer
  const handleDateClick = (info) => {
    setFormMode('add');
    setSelectedDate(info.dateStr);
    setFormData({
      clientName: '',
      startTime: `${info.dateStr}T09:00`,
      endTime: `${info.dateStr}T10:00`,
      description: '',
    });
    setDrawerOpen(true);
  };

  // Open View Booking Drawer
  const handleEventClick = (info) => {
    const event = info.event;
    setFormMode('view');
    setSelectedEvent({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      description: event.extendedProps.description || '',
    });
    setDrawerOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newBooking = {
      id: Math.random().toString(36).substr(2, 9),
      title: `Booking: ${formData.clientName}`,
      start: formData.startTime,
      end: formData.endTime,
      description: formData.description,
    };
    setBookings((prev) => [...prev, newBooking]);
    setDrawerOpen(false);
  };

  const handleDelete = (id) => {
    setBookings((prev) => prev.filter((b) => b.id !== id));
    setDrawerOpen(false);
  };

  return (
    <div className="p-4 max-w-7xl mx-auto relative">
      <h2 className="text-3xl font-bold mb-6 flex items-center gap-2">
        <CalendarCheck className="w-7 h-7 text-blue-600" />
        Booking Calendar
      </h2>

      <div className="rounded-lg overflow-hidden shadow">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          events={bookings}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          height="auto"
        />
      </div>

      {/* Side Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white dark:bg-gray-900 shadow-lg border-l border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        } z-50`}
      >
        <div className="p-5 h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold">
              {formMode === 'add'
                ? `New Booking (${selectedDate})`
                : selectedEvent?.title}
            </h3>
            <button
              onClick={() => setDrawerOpen(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Add Form */}
          {formMode === 'add' && (
            <form
              onSubmit={handleSubmit}
              className="flex-1 flex flex-col justify-between"
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Client Name
                  </label>
                  <input
                    type="text"
                    value={formData.clientName}
                    onChange={(e) =>
                      setFormData({ ...formData, clientName: e.target.value })
                    }
                    required
                    placeholder="Enter full name"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Start Time
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.startTime}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        startTime: e.target.value,
                      })
                    }
                    required
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    End Time
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.endTime}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        endTime: e.target.value,
                      })
                    }
                    required
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        description: e.target.value,
                      })
                    }
                    rows={3}
                    placeholder="Add notes or details (optional)"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
                  />
                </div>
              </div>

              <div className="mt-6 flex gap-2">
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 rounded-lg text-sm font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm font-medium transition"
                >
                  <Save className="w-4 h-4 inline mr-1" /> Save
                </button>
              </div>
            </form>
          )}

          {/* View Booking */}
          {formMode === 'view' && selectedEvent && (
            <div className="flex-1 flex flex-col justify-between">
              <div className="space-y-4 text-sm">
                <div>
                  <span className="font-semibold">Date & Time:</span>
                  <div className="mt-1">
                    {format(new Date(selectedEvent.start), 'PPpp')} –{' '}
                    {format(new Date(selectedEvent.end), 'PPpp')}
                  </div>
                </div>
                <div>
                  <span className="font-semibold">Description:</span>
                  <div className="mt-1 text-gray-700 dark:text-gray-300">
                    {selectedEvent.description || 'No description provided.'}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex gap-2">
                <button
                  type="button"
                  onClick={() => handleDelete(selectedEvent.id)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg text-sm font-medium transition"
                >
                  <Trash2 className="w-4 h-4 inline mr-1" /> Delete
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFormMode('add');
                    setFormData({
                      clientName: selectedEvent.title.replace('Booking: ', ''),
                      startTime: format(
                        new Date(selectedEvent.start),
                        "yyyy-MM-dd'T'HH:mm"
                      ),
                      endTime: format(
                        new Date(selectedEvent.end),
                        "yyyy-MM-dd'T'HH:mm"
                      ),
                      description: selectedEvent.description,
                    });
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm font-medium transition"
                >
                  <Edit2 className="w-4 h-4 inline mr-1" /> Edit
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BookingCalendar;
